import { useCallback, useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { isNil, isNumber } from 'lodash-es';

export function NumericTextField(props) {
  const {
    allowDecimals,
    onChange,
    onError,
    value,
    min,
    max,
    multiple,
    ignoreNonNumericInput,
    ...passThroughProps
  } = props;
  const [localValue, setLocalValue] = useState(isNil(value) ? '' : value);
  const [error, setError] = useState('');

  const showError = useCallback(
    (errorText, input) => {
      setError(errorText);
      onError?.(errorText);
      if (ignoreNonNumericInput)
        onChange({
          target: {
            name: passThroughProps.name,
            value: input,
            error: errorText,
          },
        });
    },
    [ignoreNonNumericInput, onChange, onError, passThroughProps],
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <TextField
      {...passThroughProps}
      value={localValue}
      error={props.error || Boolean(error)}
      helperText={props.helperText || error}
      onChange={handleTextInput}
    />
  );

  function handleTextInput(event) {
    setError('');
    const rawValue = getEnteredValue(event);
    setLocalValue(rawValue);
    const numericValue = rawValue === '-' ? -0 : parseFloat(rawValue);

    if (rawValue === '' && ignoreNonNumericInput) {
      return onChange({ target: { name: event.target.name, value: '' } });
    }

    if (Number.isNaN(numericValue)) {
      return showError('Not numeric', rawValue);
    }

    if (isNumber(min) && numericValue < min) {
      return showError(`Less than min of ${min}`, rawValue);
    }

    if (isNumber(max) && numericValue > max) {
      return showError(`Greater than max of ${max}`, rawValue);
    }

    if (isNumber(multiple) && numericValue % multiple) {
      return showError(`Must be multiple of ${multiple}`, rawValue);
    }

    if (rawValue !== '-') {
      onChange({
        target: { name: event.target.name, value: numericValue },
      });
    }
  }

  function getEnteredValue(event) {
    const entered = event.target.value.trim();
    return cleanInput(allowDecimals ? entered : entered.replaceAll('.', ''));
  }

  function cleanInput(input) {
    if (!ignoreNonNumericInput) return input;
    return input.replace(/[^0-9\\.]/g, '');
  }
}
